import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { formatDate } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { DataTable } from '../components/ui/DataTable';
import { Drawer } from '../components/ui/Drawer';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Button } from '../components/ui/Button';
import { TextAreaField, TextField } from '../components/ui/Field';
import type { FeatureFlagRecord } from '../components/system/types';

export const Route = createFileRoute('/_authed/system/feature-flags')({
  component: FeatureFlagsPage,
});

interface FlagsResponse {
  items: FeatureFlagRecord[];
}

interface FlagFormValues {
  key: string;
  description: string;
  enabled: boolean;
}

const flagKeyPattern = /^[a-z][a-z0-9_]{1,63}$/;

function FeatureFlagsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isSuperAdmin = useAuthStore((s) => s.hasRole('super_admin'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const flagsQuery = useQuery({
    queryKey: ['feature-flags'] as const,
    queryFn: ({ signal }) =>
      apiRequest<FlagsResponse>('/api/system/settings/feature-flags', { signal }),
  });

  const toggleMutation = useMutation({
    mutationFn: async (input: { key: string; enabled: boolean; description: string | null }) => {
      await apiRequest<{ ok: true }>(
        `/api/system/settings/feature-flags/${encodeURIComponent(input.key)}`,
        {
          method: 'PATCH',
          body: { enabled: input.enabled, description: input.description },
        },
      );
    },
    onMutate: async (input) => {
      // Optimistic toggle: flip the row immediately, roll back on error.
      await queryClient.cancelQueries({ queryKey: ['feature-flags'] });
      const previous = queryClient.getQueryData<FlagsResponse>(['feature-flags']);
      queryClient.setQueryData<FlagsResponse>(['feature-flags'], (curr) => {
        if (!curr) return curr;
        return {
          ...curr,
          items: curr.items.map((row) =>
            row.key === input.key ? { ...row, enabled: input.enabled } : row,
          ),
        };
      });
      return { previous };
    },
    onError: (err, _input, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['feature-flags'], ctx.previous);
      toast(err instanceof ApiError ? err.message : 'Could not update flag.', {
        variant: 'error',
      });
    },
    onSuccess: () => {
      toast('Saved.', { variant: 'success' });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      setPendingKey(null);
    },
  });

  const upsertMutation = useMutation({
    mutationFn: async (values: FlagFormValues) => {
      await apiRequest<{ ok: true }>(
        `/api/system/settings/feature-flags/${encodeURIComponent(values.key)}`,
        {
          method: 'PATCH',
          body: { enabled: values.enabled, description: values.description || null },
        },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      toast('Saved.', { variant: 'success' });
      setDrawerOpen(false);
    },
    onError: (err) => {
      toast(err instanceof ApiError ? err.message : 'Could not save flag.', {
        variant: 'error',
      });
    },
  });

  const items = flagsQuery.data?.items ?? [];

  const columns = useMemo<ColumnDef<FeatureFlagRecord, unknown>[]>(
    () => [
      {
        id: 'key',
        header: 'Key',
        cell: (info) => (
          <span className="font-mono text-xs font-medium text-[color:var(--color-text)]">
            {info.row.original.key}
          </span>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        cell: (info) => (
          <span className="text-sm text-[color:var(--color-text-muted)] line-clamp-2">
            {info.row.original.description ?? '—'}
          </span>
        ),
      },
      {
        id: 'enabled',
        header: 'Enabled',
        cell: (info) => {
          const row = info.row.original;
          const pending = pendingKey === row.key;
          return (
            <button
              type="button"
              role="switch"
              aria-checked={row.enabled}
              disabled={!isSuperAdmin || pending}
              onClick={(e) => {
                e.stopPropagation();
                setPendingKey(row.key);
                toggleMutation.mutate({
                  key: row.key,
                  enabled: !row.enabled,
                  description: row.description,
                });
              }}
              className={
                'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors ' +
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500 ' +
                'disabled:cursor-not-allowed disabled:opacity-50 ' +
                (row.enabled ? 'bg-[color:var(--color-success)]' : 'bg-[color:var(--color-border)]')
              }
              aria-label={`Toggle ${row.key}`}
            >
              <span
                aria-hidden="true"
                className={
                  'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ' +
                  (row.enabled ? 'translate-x-5' : 'translate-x-0.5')
                }
              />
            </button>
          );
        },
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)] tabular-nums">
            {formatDate(info.row.original.updatedAt, { withTime: true })}
          </span>
        ),
      },
    ],
    [isSuperAdmin, pendingKey, toggleMutation],
  );

  return (
    <ListShell
      title="Switch features on or off"
      description="Toggle product-level capabilities at runtime."
      primaryAction={
        isSuperAdmin ? (
          <Button
            onClick={() => setDrawerOpen(true)}
            icon={<Plus size={16} aria-hidden="true" />}
          >
            Add flag
          </Button>
        ) : null
      }
    >
      {flagsQuery.isError ? (
        <ErrorState
          title="Could not load the features"
          description={
            flagsQuery.error instanceof ApiError
              ? flagsQuery.error.message
              : 'Please try again in a moment.'
          }
          onRetry={() => flagsQuery.refetch()}
        />
      ) : (
        <DataTable<FeatureFlagRecord>
          data={items}
          columns={columns}
          isLoading={flagsQuery.isLoading && !flagsQuery.data}
          getRowId={(row) => row.key}
          emptyState={
            <EmptyState
              title="No features yet"
              description="Define a flag key and toggle features without redeploying."
              action={
                isSuperAdmin ? (
                  <Button
                    onClick={() => setDrawerOpen(true)}
                    icon={<Plus size={16} aria-hidden="true" />}
                  >
                    Add flag
                  </Button>
                ) : undefined
              }
            />
          }
        />
      )}

      <FlagDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={(values) => upsertMutation.mutateAsync(values)}
        existingKeys={items.map((i) => i.key)}
      />
    </ListShell>
  );
}

interface FlagDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: FlagFormValues) => Promise<void>;
  existingKeys: ReadonlyArray<string>;
}

function FlagDrawer({ open, onClose, onSubmit, existingKeys }: FlagDrawerProps) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FlagFormValues>({
    defaultValues: { key: '', description: '', enabled: false },
  });

  useEffect(() => {
    if (open) reset({ key: '', description: '', enabled: false });
  }, [open, reset]);

  const submit = handleSubmit(async (values) => {
    const key = values.key.trim();
    if (!flagKeyPattern.test(key)) {
      setError('key', {
        message: 'Use snake_case: lowercase letters, digits, underscores. 2–64 chars.',
      });
      return;
    }
    if (existingKeys.includes(key)) {
      setError('key', { message: 'A flag with this key already exists.' });
      return;
    }
    await onSubmit({
      key,
      description: values.description.trim(),
      enabled: values.enabled,
    });
  });

  return (
    <Drawer open={open} onClose={onClose} title="New on/off switch" size="md">
      <form onSubmit={submit} noValidate className="space-y-4">
        <TextField
          label="Key"
          autoComplete="off"
          placeholder="e.g. payments_razorpay_v2"
          hint="Used in code as the lookup id. Cannot be changed later."
          error={errors.key?.message}
          {...register('key', { required: 'Required' })}
        />
        <TextAreaField
          label="Description"
          rows={3}
          optional
          placeholder="What does this flag control?"
          error={errors.description?.message}
          {...register('description')}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-[color:var(--color-border)] text-gold-700 focus:ring-gold-500"
            {...register('enabled')}
          />
          Enable immediately
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            Create flag
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
